export interface LaidOutFlow<T> { nodes: T[]; edges: { from: string; to: string }[]; columns: number; width: number; height: number; hasProgression: boolean; }
export const FLOW_NODE_WIDTH = 264;
export const FLOW_NODE_HEIGHT = 190;
export const FLOW_COLUMN_PITCH = 350;
export const FLOW_HORIZONTAL_PADDING = 24;
const ROW_PITCH = 222;
const TOP_PADDING = 70;

export function layoutFlowNodes<T extends { id: string; parents: string[]; column: number; x: number; y: number }>(nodes: T[], nodeHeight = FLOW_NODE_HEIGHT): LaidOutFlow<T> {
  const rowPitch = nodeHeight + ROW_PITCH - FLOW_NODE_HEIGHT;
  const nodeById = new Map(nodes.map(node => [node.id, node]));
  for (const node of nodes) {
    // Sources always point to earlier steps; the fallback preserves a usable graph for old data.
    node.column = node.parents.length ? Math.max(node.column, Math.max(...node.parents.map(id => nodeById.get(id)?.column ?? 0)) + 1) : node.column;
  }
  const columns = Math.max(1, ...nodes.map(node => node.column + 1));
  for (let column = 0; column < columns; column++) {
    const layer = nodes.filter(node => node.column === column);
    let previousBottom = TOP_PADDING - (rowPitch - nodeHeight);
    layer.sort((a, b) => {
      const average = (node: T) => node.parents.length
        ? node.parents.reduce((sum, id) => sum + (nodeById.get(id)?.y ?? 0), 0) / node.parents.length : 0;
      return average(a) - average(b) || nodes.indexOf(a) - nodes.indexOf(b);
    });
    layer.forEach((node, index) => {
      const desired = node.parents.length
        ? node.parents.reduce((sum, id) => sum + (nodeById.get(id)?.y ?? 0), 0) / node.parents.length : TOP_PADDING + index * rowPitch;
      node.x = FLOW_HORIZONTAL_PADDING + column * FLOW_COLUMN_PITCH;
      node.y = Math.max(TOP_PADDING + index * rowPitch, desired, previousBottom + rowPitch - nodeHeight);
      previousBottom = node.y + nodeHeight;
    });
  }
  const edges = nodes.flatMap(node => node.parents.filter(parent => nodeById.has(parent)).map(from => ({ from, to: node.id })));
  return {
    nodes, edges, columns,
    width: FLOW_HORIZONTAL_PADDING * 2 + (columns - 1) * FLOW_COLUMN_PITCH + FLOW_NODE_WIDTH,
    height: Math.max(280, ...nodes.map(node => node.y + nodeHeight + 24)),
    hasProgression: edges.length > 0,
  };
}
